/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {combineLatest, EMPTY, forkJoin, from, merge, Observable, of, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {PopoverDirective} from 'ngx-bootstrap/popover';
import {ToastrService} from 'ngx-toastr';
import {RegulationTreeService} from '../../../+configuration/regulation-tree/regulation-tree.service';
import {PayEventsService} from '../../../+payrolls';
import {DATE_MAX, DATE_MIN, ProfileMode} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {CliEmpAttributes, CliReg, SsoTypeEnum} from '../../../models/models.interfaces';
import {CliCorePropertiesService} from '../../../navigation/cli-core-properties.service';
import {
  GeneratedSimpleFormComponent
} from '../../../shared/form-generator/generated-simple-form/generated-simple-form.component';
import {AnyData, Data} from '../../api';
import {ValueData, ZoneType} from '../../employees/contract/contract.interfaces';
import {Contract} from '../../employees/employees.model';
import {EmployeesService} from '../../employees/employees.service';
import {DataTransformer} from '../../form-generator/data-transformer/data-transformer';
import {FormCardFieldComponent} from '../../form-generator/form-card/form-card-field/form-card-field.component';
import {countries, FormDescriptorService} from '../../form-generator/form-descriptor.service';
import {FormFieldValidationComponent} from '../../form-generator/form-field-validation/form-field-validation.component';
import {
  FormDescriptor,
  GroupZoneDescriptor,
  GroupZoneType,
  ZoneDescriptor,
  ZoneValues
} from '../../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../../form-generator/form-generator.service';
import {FormGeneratorUtils} from '../../form-generator/form-generator.utils';
import {Validators as GpValidators} from '../../forms';
import {ModalService} from '../../modal';
import {CanComponentDeactivate} from '../../routing/can-deactivate-guard';
import {TreeNode, WizardComponent} from '../../ui';
import {isMobileDevice} from '../../utils/browser';
import {isPresent} from '../../utils/lang';
import {Objects} from '../../utils/objects';
import {ProcessInstance} from '../../workflow/workflow-v2.interface';
import {HireOptionsCopyComponent} from '../hire-options-copy/hire-options-copy.component';
import {HireUtilsService, zoneTypeHasTransId} from '../hire-utils.service';
import {EmpModelOption, EmpModelOptionValue, EntityPaySerie} from '../hire.interface';
import {HireService} from '../hire.service';

export const onChangeAutoNumber = (value: boolean, relatedControl: AbstractControl) => {
  if (value) {
    relatedControl.setValue('');
  }
};
/**
 * Constructs a validation function that checks that at least one of the field has been filled.
 */
export const atLeastOneRequiredOf = (...fields: string[]) => (group: UntypedFormGroup): { [key: string]: any } => {
  return fields.some((field: string) => !!group.controls[field].value) ? null : {valueMissing: true};
};

export const validateEndDate = (beginDate: string, endDate: string) => (group: UntypedFormGroup): {
  [key: string]: any
} => {
  const constraint = (moment(group.controls[endDate].value).isBefore(group.controls[beginDate].value));
  return constraint ? {'beginAfterEnd': true} : null;
};

export const buildParams = (employeeData: any): string[] => {
  const params: string[] = [];

  // Check firstName and lastName and maidenName and ssn matches
  Objects.removeSpaces(employeeData);

  if (employeeData.firstName) {
    params.push(`first-name=${employeeData.firstName}`);
  }

  if (employeeData.lastName) {
    params.push(`last-name=${employeeData.lastName}`);
  }

  if (employeeData.maidenName) {
    params.push(`maiden-name=${employeeData.maidenName}`);
  }

  if (employeeData.ssn) {
    params.push(`national-id=${employeeData.ssn}`);
  }
  return params;
};

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'gp-hire-employee',
  templateUrl: './hire-employee.component.html',
  styleUrls: ['./hire-employee.component.scss']
})
export class HireEmployeeComponent extends Destroyable implements OnInit, AfterViewInit, CanComponentDeactivate {

  public onChangeAutoNumber = onChangeAutoNumber;

  @Input()
  public employeeFileEnabled: boolean = true;

  @Input()
  public profileMode?: ProfileMode;

  @ViewChild(PopoverDirective)
  public natIdPopover: PopoverDirective;

  @ViewChild(HireOptionsCopyComponent)
  public hireOptionsCopyComponent: HireOptionsCopyComponent;

  @ViewChild(WizardComponent)
  public wizard: WizardComponent;

  public countries: string[] = countries;
  public translateCountries: any[] = [];

  public duplicateContract: Contract;

  public registrationNumberAuto: boolean = true;
  public contractIdAuto: boolean = true;

  public loadingDuplicates: boolean = false;
  public duplicates: Array<Data<CliEmpAttributes>> = [];

  // Employee that might have been selected from the duplicate list
  public selectedEmployee: Data<CliEmpAttributes> = null;
  public employeeSelectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public paySeriesTitle: string = '';
  public paySeries: Array<EntityPaySerie> = null;
  public paySeriesTree: Array<TreeNode<EntityPaySerie>> = null;
  public selectedPaySerieId: number;
  public selectedCliRegId: number;
  public isEmailMandatory: boolean = false;

  public initialForm: UntypedFormGroup;
  public infoForm: UntypedFormGroup;
  public numberGroup: UntypedFormGroup;
  public contractIdGroup: UntypedFormGroup;
  public contractForm: UntypedFormGroup;
  public regulationForm: UntypedFormGroup;
  public contractFormGroup: UntypedFormGroup;
  public infoAndContractForm: UntypedFormArray;
  public optionToCopyForm: UntypedFormGroup;
  public employeeModelForm: UntypedFormGroup;

  // Loading flag when submitting the form
  public creating: boolean = false;
  public employeeId: number;
  public employeeIdValidation: boolean = false;
  public contractIdValidation: boolean = false;
  public contracts: Contract[] = [];

  public selectedEmpModelOptions: Array<EmpModelOption>;
  public isMobileDevice: boolean;
  public contractIdHiredFromMobile: number = null;

  public regulations: Array<Data<CliReg>> = null;
  public regulationTree: Array<TreeNode<CliReg>> = null;
  public missingForceRequiredZones = false;
  public cliEmpL1GroupZoneDescriptor: GroupZoneDescriptor;
  public cliEmpL1ZoneValues: ZoneValues;
  public cliEmpL2GroupZoneDescriptor: GroupZoneDescriptor;
  public cliEmpL2ZoneValues: ZoneValues;
  public ssoType: SsoTypeEnum;
  @ViewChild('cliEmpL2Form')
  public cliEmpL2Form: GeneratedSimpleFormComponent;

  public get selectedPaySerieValue(): string {
    const paySerie = this.contractForm.get('paySerie').value;

    if (!isPresent(paySerie) || !isPresent(this.paySeries)) {
      return '';
    }

    const selected = this.paySeries.find(serie => serie.id === paySerie);

    return selected ? this.translate.instant(selected.attributes.name) : '';
  }

  private subscribedControls: Subscription[] = [];
  private regulationZoneDescriptors: Array<ZoneDescriptor> = new Array<ZoneDescriptor>();
  private modelZoneDescriptors: Map<string, ZoneDescriptor> = new Map<string, ZoneDescriptor>();
  private regulationNickName: string;

  private static readonly VALUE_TRANSLATED_TYPES: ReadonlyArray<ZoneType> = [
    ZoneType.LIST, ZoneType.TREE, ZoneType.ENTITY, ZoneType.ENTITY_DATA, ZoneType.ENTITY_PREFIXED
  ];

  constructor(private formBuilder: UntypedFormBuilder,
              private changeDetectorRef: ChangeDetectorRef,
              private employeesService: EmployeesService,
              private modalService: ModalService,
              private router: Router,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private formDescriptorService: FormDescriptorService,
              private hireUtilsService: HireUtilsService,
              private toastrService: ToastrService,
              private payEventsService: PayEventsService,
              private cliCorePropertiesService: CliCorePropertiesService,
              private regulationTreeService: RegulationTreeService,
              private formGeneratorService: FormGeneratorService,
              private hireService: HireService) {
    super();
    this.isMobileDevice = isMobileDevice();
    this.cliCorePropertiesService.ssoTypeBehaviorSubject
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(value => this.ssoType = value as SsoTypeEnum);

    this.numberGroup = formBuilder.group({
      number: ['', {value: '', disabled: true, asyncValidators: this.isEmployeeIDExistValidator.bind(this)}],
      auto: [true]
    }, {
      validator: atLeastOneRequiredOf('number', 'auto')
    });

    this.initialForm = formBuilder.group({
      paySerie: ['', Validators.required],
      regulation: ['', Validators.required]
    });

    this.infoForm = formBuilder.group({
      Sex: [''],
      number: this.numberGroup,
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      maidenName: [''],
      email: [{value: '', disabled: true}, {
        validators: [GpValidators.emailValidator],
        asyncValidators: [this.employeesService.uniqueEmailAsyncValidator()]
      }],
      dob: ['', GpValidators.validateISO8601Date],
      ssn: ['']
    });

    this.infoFormValueChanges();

    this.contractIdGroup = formBuilder.group({
      contractId: ['', {value: '', disabled: true, asyncValidators: this.isContractIDExistValidator.bind(this)}],
      auto: [true]
    }, {
      validator: atLeastOneRequiredOf('contractId', 'auto')
    });

    this.contractForm = formBuilder.group({
      contractId: this.contractIdGroup,
      beginDate: ['', GpValidators.validateISO8601Date],
      forecastedEndDate: ['', [GpValidators.validateISO8601Date]],
      paySerie: ['', Validators.required]
    }, {
      validator: validateEndDate('beginDate', 'forecastedEndDate')
    });

    this.contractForm.get('paySerie').valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((id: number) => {
        const selectedPaySerie: AnyData = this.paySeries.find(serie => serie.id === id);
        const emailControl: UntypedFormControl = <UntypedFormControl>this.infoForm.get('email');
        const paySerieAutoCreate = selectedPaySerie && selectedPaySerie.attributes.userAutoCreate;
        const validators: ValidatorFn[] = [GpValidators.emailValidator];
        if (paySerieAutoCreate && this.ssoType !== SsoTypeEnum.IPS_SSO) {
          validators.push(Validators.required);
          this.isEmailMandatory = true;
        } else {
          this.isEmailMandatory = false;
        }
        emailControl.setValidators(validators);
        emailControl.updateValueAndValidity();
      });

    this.contractForm.get('beginDate').valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
          this.contractForm.updateValueAndValidity();
        }
      );

    this.regulationForm = this.formBuilder.group({});
    this.optionToCopyForm = this.formBuilder.group({});
    this.employeeModelForm = this.formBuilder.group({});

    this.contractFormGroup = this.formBuilder.group({
      contractForm: this.contractForm,
      regulationForm: this.regulationForm
    });

    this.infoAndContractForm = this.formBuilder.array([
      this.contractFormGroup,
      this.infoForm,
      this.optionToCopyForm,
      this.employeeModelForm
    ]);
  }

  private infoFormValueChanges(): void {
    merge(
      combineLatest([
        this.infoForm.get('firstName').valueChanges.pipe(distinctUntilChanged()),
        this.infoForm.get('lastName').valueChanges.pipe(distinctUntilChanged())
      ]),
      combineLatest([
        this.infoForm.get('firstName').valueChanges.pipe(distinctUntilChanged()),
        this.infoForm.get('lastName').valueChanges.pipe(distinctUntilChanged()),
        this.infoForm.get('maidenName').valueChanges.pipe(distinctUntilChanged())
      ]),
      combineLatest([
        this.infoForm.get('lastName').valueChanges.pipe(distinctUntilChanged()),
        this.infoForm.get('firstName').valueChanges.pipe(distinctUntilChanged()),
        this.infoForm.get('ssn').valueChanges.pipe(distinctUntilChanged())
      ])
    ).pipe(
      debounceTime(650),
      switchMap(() => {
        if (!this.employeeId) {
          return this.checkDuplicates();
        }
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public ngOnInit() {
    this.route.params
      .pipe(
        takeUntil(this.componentDestroyed$),
        map((params: any) => this.employeeId = +params.id || undefined),
        switchMap(() => forkJoin([
            this.employeeId ? this.employeesService.getEmployeeById(this.employeeId) : of(null),
            this.employeeId ? this.employeesService.getPartialEmployee(this.employeeId) : of(null)
          ]
        ))
      )
      .subscribe(([fullEmployee, partialEmployee]) => {
        if (fullEmployee && partialEmployee) {
          this.contracts = fullEmployee.attributes.empContracts;
          this.selectedEmployee = partialEmployee;
          this.selectedEmployee.userEmail = fullEmployee.attributes.userEmail;
          this.selectedEmployee.email = fullEmployee.attributes.email;
          this.selectedEmployee.userActive = fullEmployee.attributes.userActive;
          this.selectedEmployee.birthDate = fullEmployee.attributes.birthDate;
          this.selectedEmployee.nationalId = fullEmployee.attributes.nationalId;
          this.infoForm.get('firstName').setValue(this.selectedEmployee.firstName);
          this.infoForm.get('lastName').setValue(this.selectedEmployee.lastName);
          this.infoForm.get('dob').setValue(this.selectedEmployee.birthDate);
          this.infoForm.get('ssn').setValue(this.selectedEmployee.nationalId);
          this.infoForm.get('email').setValue(this.selectedEmployee.email);
        }
        this.updateEmail();
      });

    this.numberGroup.get('auto').valueChanges.subscribe(value => {
      const numberControl = this.numberGroup.get('number');
      value ? numberControl.disable() : numberControl.enable();
    });

    this.contractIdGroup.get('auto').valueChanges.subscribe(value => {
      const contractIdControl = this.contractIdGroup.get('contractId');
      value ? contractIdControl.disable() : contractIdControl.enable();
    });

    // Initialize pay serie ID
    this.formDescriptorService.getPaySeries()
      .subscribe((response: { data: Array<EntityPaySerie>, transId: string }) => {
        this.paySeries = response.data;
        this.paySeriesTitle = this.translate.instant(response.transId);
        this.paySeriesTree = this.hireUtilsService.createTree(this.paySeries);
      });

    this.translate.onLangChange
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$),
        startWith({lang: this.translate.currentLang, translations: []})
      )
      .subscribe(() => this.translateCountries = this.getTranslateCountries());

    this.employeeModelForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.selectedEmpModelOptions = Object.keys(this.employeeModelForm.controls)
          .filter(familyName => this.employeeModelForm.get(familyName).value)
          .map(familyName => this.employeeModelForm.get(familyName).value);
      });
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  public canDeactivate(): boolean | Observable<boolean> {
    if (this.infoForm.dirty || this.initialForm.dirty) {
      return from(
        this.modalService.dynamicConfirmModal({
          'title': 'DIALOG.you_have_unsaved_changes',
          'text': 'DIALOG.you_have_unsaved_changes?',
          'confirmButton': 'FORMS.discard_all_changes',
          'cancelButton': 'FORMS.keep_editing'
        })
      );
    }

    return true;
  }

  public regulationSelected(cliRegId: number) {
    this.cliEmpL1GroupZoneDescriptor = null;
    this.cliEmpL2GroupZoneDescriptor = null;
    this.cliEmpL1ZoneValues = null;
    this.cliEmpL2ZoneValues = null;
    this.modelZoneDescriptors = new Map<string, ZoneDescriptor>();
    this.regulationForm.get('regulation').setValue(cliRegId);
    if (!cliRegId) {
      return;
    }
    this.formDescriptorService.getEmpModelZoneDescriptors(cliRegId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(descriptors => this.modelZoneDescriptors = descriptors);
    this.formDescriptorService.getFormDescriptor(cliRegId, this.employeeFileEnabled ? null : 'MSS')
      .subscribe(formDescriptor => {
        this.initializeCliEmpForm(formDescriptor);
      });
  }

  private initializeCliEmpForm(formDescriptor: FormDescriptor) {
    const forceRequired = this.isEmailMandatory ? ['cliEmp.firstName', 'cliEmp.lastName', 'cliEmp.email'] : ['cliEmp.firstName', 'cliEmp.lastName'];
    const line1 = ['cliEmp.firstName', 'cliEmp.lastName', 'cliEmp.maidenName'];
    const line2 = ['cliEmp.birthDate', 'cliEmp.nationalId', 'cliEmp.email'];
    const zoneDescriptors = formDescriptor.tabSheets
      .flatMap(tabSheet => tabSheet.groupZones)
      .flatMap(groupZone => groupZone.zones)
      .filter(zoneDescriptor => line1.includes(zoneDescriptor.zoneID) || line2.includes(zoneDescriptor.zoneID))
      .map(zoneDescriptor => {
        zoneDescriptor.displayorder = line1.indexOf(zoneDescriptor.zoneID) || line2.indexOf(zoneDescriptor.zoneID);
        zoneDescriptor.nbColumns = 1;
        if (zoneDescriptor.zoneID === 'cliEmp.birthDate') {
          zoneDescriptor.defaultValue = null;
        }
        // force all zones to be not required except force required ones
        zoneDescriptor.isRequired = forceRequired.includes(zoneDescriptor.zoneID);
        return zoneDescriptor;
      });
    const zoneNames = zoneDescriptors.map(zoneDescriptor => zoneDescriptor.zoneID);
    this.missingForceRequiredZones = forceRequired.some(forceRequiredZone => !zoneNames.includes(forceRequiredZone));
    this.cliEmpL1GroupZoneDescriptor = this.formGeneratorService.createEmptyGroupZoneDescriptor('cliEmpL1', zoneDescriptors
      .filter(zoneDescriptor => line1.includes(zoneDescriptor.zoneID)));
    this.cliEmpL2GroupZoneDescriptor = this.formGeneratorService.createEmptyGroupZoneDescriptor('cliEmpL2', zoneDescriptors
      .filter(zoneDescriptor => line2.includes(zoneDescriptor.zoneID)));
    let dataL1: Array<ZoneValues> = [];
    let dataL2: Array<ZoneValues> = [];
    if (this.selectedEmployee) {
      dataL1 = [
        {
          isDated: false,
          groupName: 'cliEmpL1',
          begindate: DATE_MIN,
          enddate: DATE_MAX,
          values: {
            'cliEmp.firstName': {
              value: this.selectedEmployee.firstName,
              type: 'LABEL'
            },
            'cliEmp.lastName': {
              value: this.selectedEmployee.lastName,
              type: 'LABEL'
            },
            'cliEmp.maidenName': {
              value: this.selectedEmployee.maidenName,
              type: 'LABEL'
            }
          }
        }
      ];
      dataL2 = [
        {
          isDated: false,
          groupName: 'cliEmpL2',
          begindate: DATE_MIN,
          enddate: DATE_MAX,
          values: {
            'cliEmp.birthDate': {
              value: this.selectedEmployee.birthDate,
              type: 'DATE'
            },
            'cliEmp.nationalId': {
              value: this.selectedEmployee.nationalId,
              type: 'LABEL'
            },
            'cliEmp.email': {
              value: this.selectedEmployee.email,
              type: 'LABEL'
            }
          }
        }
      ];
    }
    this.cliEmpL1ZoneValues = DataTransformer
      .createZoneValues(this.cliEmpL1GroupZoneDescriptor, this.formGeneratorService, dataL1)[0];
    this.cliEmpL2ZoneValues = DataTransformer
      .createZoneValues(this.cliEmpL2GroupZoneDescriptor, this.formGeneratorService, dataL2)[0];

    this.setValidatorsOnInfoForm('cliEmp.firstName', this.cliEmpL1GroupZoneDescriptor, 'firstName');
    this.setValidatorsOnInfoForm('cliEmp.lastName', this.cliEmpL1GroupZoneDescriptor, 'lastName');
    this.setValidatorsOnInfoForm('cliEmp.maidenName', this.cliEmpL1GroupZoneDescriptor, 'maidenName');
    this.setValidatorsOnInfoForm('cliEmp.birthDate', this.cliEmpL2GroupZoneDescriptor, 'dob');
    this.setValidatorsOnInfoForm('cliEmp.nationalId', this.cliEmpL2GroupZoneDescriptor, 'ssn');
    this.setValidatorsOnInfoForm('cliEmp.email', this.cliEmpL2GroupZoneDescriptor, 'email');
    this.infoForm.updateValueAndValidity();
  }

  public onCliEmpFormValuesChanged(values) {
    if (values['cliEmpL1']) {
      this.infoForm.get('firstName')
        .setValue(this.getZoneRawValue(this.cliEmpL1GroupZoneDescriptor, 'cliEmp.firstName', values));
      this.infoForm.get('lastName')
        .setValue(this.getZoneRawValue(this.cliEmpL1GroupZoneDescriptor, 'cliEmp.lastName', values));
      this.infoForm.get('maidenName')
        .setValue(this.getZoneRawValue(this.cliEmpL1GroupZoneDescriptor, 'cliEmp.maidenName', values));
    }
    if (values['cliEmpL2']) {
      this.infoForm.get('dob')
        .setValue(this.getZoneRawValue(this.cliEmpL2GroupZoneDescriptor, 'cliEmp.birthDate', values));
      this.infoForm.get('ssn')
        .setValue(this.getZoneRawValue(this.cliEmpL2GroupZoneDescriptor, 'cliEmp.nationalId', values));
      this.infoForm.get('email')
        .setValue(this.getZoneRawValue(this.cliEmpL2GroupZoneDescriptor, 'cliEmp.email', values));
    }
  }

  private setValidatorsOnInfoForm(zoneName: string, groupZoneDescriptor: GroupZoneDescriptor, formControlName: string) {
    const zoneDescriptor = groupZoneDescriptor.zones.find(zd => zd.zoneID === zoneName);
    if (zoneDescriptor) {
      this.infoForm.get(formControlName).setValidators(FormFieldValidationComponent.buildValidators(
        zoneDescriptor, GroupZoneType.UNIQUE_MANDATORY, []
      ));

      if (zoneDescriptor.zoneID === 'cliEmp.email') {
        this.infoForm.get(formControlName).setAsyncValidators(this.employeesService.uniqueEmailAsyncValidator());
      }
    }
  }

  private getZoneRawValue(groupZoneDescriptor: GroupZoneDescriptor, zoneName: string, values: any): any {
    const values0 = values[groupZoneDescriptor.groupZoneID][0].values;
    const zoneDescriptor = groupZoneDescriptor.zones.find(zd => zd.zoneID === zoneName);
    const value = values0[zoneName]?.value;
    if (value && zoneDescriptor.inputMask) {
      return DataTransformer.extractMask(value, zoneDescriptor.inputMask);
    }
    return value;
  }

  public get selectedRegulationNickName(): string {
    const regulation = this.regulationForm.get('regulation').value;

    if (!isPresent(regulation) || !isPresent(this.regulations)) {
      return '';
    }
    const selected: Data<CliReg> = this.regulations.find((r: Data<CliReg>) => r.id === regulation);

    return selected ? selected.attributes.nickName : '';
  }

  public regulationChange(regulation: { cliRegId: number, cliRegNickname: string }) {
    if (regulation) {
      this.selectedCliRegId = regulation.cliRegId;
      this.regulationNickName = regulation.cliRegNickname;
      if (!regulation.cliRegId) {
        this.unsubscribeControls();
        return;
      }
    } else {
      this.selectedCliRegId = null;
      this.regulationNickName = null;
    }

    this.resetForm(this.employeeModelForm);
  }

  public paySerieSelected(id: number) {
    this.selectedPaySerieId = id;
    this.selectedCliRegId = null;
    this.initialForm.get('regulation').reset();
    this.contractForm.get('paySerie').setValue(id);
    if (!id) {
      this.unsubscribeControls();
      return;
    }
    this.payEventsService.getCliRegList(id)
      .subscribe((response: { data: Array<Data<CliReg>>, transId: string }) => {
        this.regulations = response.data;
        this.regulationTree = this.regulationTreeService.createTree(this.regulations);
      });
  }

  public finalize() {
    this.creating = true;
    if (this.employeeFileEnabled) {
      this.collectDataValues().pipe(
        switchMap(data => this.saveEmployee(data)),
        finalize(() => this.creating = false)
      ).subscribe((contractData: { contractId: number, isInPopulation: boolean }) => {
        this.infoForm.markAsPristine();
        this.initialForm.markAsPristine();
        if (this.isMobileDevice) {
          this.contractIdHiredFromMobile = contractData.contractId;
        } else if (contractData.isInPopulation) {
          this.router.navigate(['employees/files', contractData.contractId]);
        } else {
          this.router.navigate(['employees/files']);
        }
      });
    } else {
      this.collectDataValues().pipe(
        switchMap(data => this.launchWorkflowV2(data)),
        finalize(() => this.creating = false)
      ).subscribe(() => this.router.navigate(['/inbox/requests']));
    }
  }

  public canCheckDuplicates() {
    const employeeData: any = this.infoForm.value;
    return employeeData.firstName && employeeData.lastName;
  }

  public openDuplicateDetails(employee: any) {
    this.duplicateContract = employee?.empContracts.find((c: Contract) => c.mainContract)
      || employee?.empContracts[0];

    this.modalService.openModalWithId('employee-detail');
  }

  public toggleSelectEmployee(employee: Data<CliEmpAttributes>) {
    if (this.selectedEmployee && this.selectedEmployee.id === employee.id) {
      this.selectedEmployee = null;
      this.resetForm(this.employeeModelForm);
      this.updateEmail();
    } else {
      this.modalService.confirm({
        'title': 'DIALOG.take_record',
        'text': 'DIALOG.confirm_take_record?'
      }).then((confirmed: boolean) => {
        if (confirmed) {
          this.selectedEmployee = employee;
          this.contracts = this.selectedEmployee.empContracts;
          this.resetForm(this.employeeModelForm);
          this.updateEmail();
          setTimeout(() => this.wizard.next());
        }
      });
    }
  }

  public showPopover() {
    setTimeout(() => {
      this.natIdPopover.show();
    });
  }

  public checkDuplicates(): Observable<Array<Data<CliEmpAttributes>>> {
    // Reset possible previously selected duplicate
    if (this.selectedEmployee) {
      this.selectedEmployee = null;
      this.updateEmail();
    }
    this.duplicates = undefined;
    if (this.canCheckDuplicates()) {
      return this.proccessCheckDuplicates();
    }
    return EMPTY;
  }

  private proccessCheckDuplicates(): Observable<Array<Data<CliEmpAttributes>>> {
    const params = buildParams(this.infoForm.value);

    this.loadingDuplicates = true;
    return this.employeesService.getDuplicates(params.join('&'), this.profileMode)
      .pipe(
        tap((result) => this.duplicates = result),
        finalize(() => {
          this.loadingDuplicates = false;
          if (this.isMobileDevice && this.duplicates?.length && !this.toastrService.currentlyActive) {
            this.toastrService.info(this.translate.instant('EMPLOYEE_HIRE.possible_duplicates'), null, {timeOut: 7000});
          }
        })
      );
  }

  private saveEmployee(data: any): Observable<any> {
    if (this.selectedEmployee === null) {

      return this.employeesService.createEmployee(data)
        .pipe(map(result => {
          return {contractId: result.data.empContractId, isInPopulation: result.isInPopulation};
        }));
    } else {
      const queryParams: string[] = [];

      if (this.hireOptionsCopyComponent && this.hireOptionsCopyComponent.optionsForCopy.selectedCopyInfoOption) {
        queryParams.push(`hireCopyInfoValue=${this.hireOptionsCopyComponent.optionsForCopy.selectedCopyInfoOption}`);
        queryParams.push(`sourceContractId=${this.hireOptionsCopyComponent.optionsForCopy.selectedContract.id}`);
      }

      const params: string = queryParams.length ? ('?' + queryParams.join('&')) : '';

      return this.employeesService.addContract(this.selectedEmployee.id, data, params)
        .pipe(map(result => {
          return {contractId: result.data.id, isInPopulation: result.isInPopulation};
        }));
    }
  }

  private launchWorkflowV2(data: { [key: string]: any }): Observable<ProcessInstance> {
    const cliEmpId = this.selectedEmployee ? this.selectedEmployee.id : null;
    const employee = this.getEmployee();
    return this.hireService.createUpdatePersonalInfoWorkflowV2(
      data,
      'mssHiring',
      this.selectedCliRegId,
      employee.firstName.trim(),
      employee.lastName.trim(),
      cliEmpId,
      this.profileMode
    );
  }

  private collectDataValues() {
    const paySerieId = this.findPaySerie(this.contractForm.getRawValue().paySerie);
    const paySerie = this.paySeries.find(serie => serie.id === this.contractForm.getRawValue().paySerie);

    const selectedRehireProfile = this.hireOptionsCopyComponent?.optionsForCopy?.items
      .find(item => item.value === '' + this.hireOptionsCopyComponent.optionsForCopy.selectedCopyInfoOption);

    const rehireProfile = selectedRehireProfile ? selectedRehireProfile : null;

    let contractData: { [p: string]: any } = this.hireUtilsService.getAllContractData(
      this.getEmployee(), this.contractForm.getRawValue(), this.regulationForm.getRawValue(), this.regulationNickName,
      paySerieId, paySerie, this.paySeries,
      !this.selectedEmployee && !this.employeeId,
      rehireProfile);

    contractData = this.addAdditionalContractData(contractData);
    contractData = this.addContractModels(contractData);
    contractData = this.hireUtilsService.cleanContractData(contractData);
    return of(contractData);
  }

  public addAdditionalContractData(contractData: { [key: string]: any }) {
    const result = {...contractData};
    const regulationData = this.regulationForm.getRawValue();
    this.regulationZoneDescriptors.forEach(zoneDescriptor => {
      const {zoneGroup, zoneID} = zoneDescriptor;
      // FIXME
      //  Dirty fix of GS-554
      //  Should handle all list types (LIST, ENTITY, TREE, PEOPLE_TREE) with the correct values and not the transId of
      //  the corresponding rep_list value
      if (zoneID === 'PayrollSets') {
        return;
      }

      if (!result[zoneGroup]) {
        result[zoneGroup] = this.formGeneratorService.getGroupStructure(zoneGroup, {});
      }

      const value = regulationData[FormGeneratorUtils.sanitizeZoneName(zoneID)];
      result[zoneGroup][0].values[zoneID] = this.hireUtilsService.getValueData(zoneDescriptor, value);
    });
    return result;
  }

  public addContractModels(contractData: { [key: string]: any }): { [key: string]: any } {
    const result = {...contractData};

    this.selectedEmpModelOptions
      .map((empModelOption: EmpModelOption) => empModelOption.values)
      .forEach((selectedValues: Array<EmpModelOptionValue>) => selectedValues.forEach(this.populateValues(result)));
    return result;
  }

  private populateValues(contractData: { [key: string]: any }) {
    return ({groupName, zoneValue, zoneTransId, zoneType, zoneName}: EmpModelOptionValue) => {
      if (!contractData[groupName]) {
        contractData[groupName] = this.formGeneratorService.getGroupStructure(groupName);
      }

      contractData[groupName][0].values = Object.assign({
          [zoneName]: this.resolveModelOptionValueData(zoneName, zoneValue, zoneType, zoneTransId)
        },
        contractData[groupName][0].values);
    };
  }

  private resolveModelOptionValueData(zoneName: string, zoneValue: string, zoneType: ZoneType, zoneTransId: string): ValueData {
    const descriptor = this.modelZoneDescriptors.get(zoneName);
    if (descriptor && HireEmployeeComponent.VALUE_TRANSLATED_TYPES.includes(zoneType)) {
      const valueData = this.hireUtilsService.getValueData(descriptor, zoneValue);
      const hasMatchingItem = (descriptor.metadatas?.items || [])
        .some(item => item.display && (item.value + '') === (valueData.value + ''));
      if (hasMatchingItem) {
        return valueData;
      }
    }

    const isEntityType = zoneType === ZoneType.ENTITY || zoneType === ZoneType.ENTITY_PREFIXED || zoneType === ZoneType.ENTITY_DATA;
    const valueData: ValueData = {
      type: zoneType,
      value: isEntityType ? +zoneValue : zoneValue
    };
    if (zoneTypeHasTransId(zoneType)) {
      valueData.transId = zoneTransId;
    }
    return valueData;
  }

  private getEmployee(): any {
    if (this.selectedEmployee) {
      Object.assign(this.selectedEmployee, {
        firstName: this.selectedEmployee.firstName || '',
        lastName: this.selectedEmployee.lastName || '',
        maidenName: this.selectedEmployee.maidenName || '',
        number: {number: this.selectedEmployee.otherid1 || ''},
        ssn: this.selectedEmployee.nationalId || '',
        email: this.selectedEmployee.email || '',
        dob: this.selectedEmployee.dob || ''
      });

      return this.selectedEmployee;
    }

    return this.infoForm.value;
  }

  private findPaySerie(legalOrgId: number) {
    const legalOrg = this.paySeries.find(serie => serie.id === legalOrgId);

    return legalOrg.attributes.paySerieID || this.findPaySerie(legalOrg.attributes.parent);
  }

  private getTranslateCountries(): any[] {

    return this.countries.map((element: string) => ({
      country: this.translate.instant(`EMPLOYEE_HIRE.Country.${element}`),
      format: this.translate.instant(`EMPLOYEE_HIRE.COUNTRY_FORMAT.${element}`)
    })).sort((a, b) => a.country > b.country ? 1 : -1);
  }

  public fieldsChange(fields: Array<FormCardFieldComponent>) {
    fields.forEach(item => {
      this.bindConfidentialityValue(item);
      this.regulationZoneDescriptors.push(item.descriptor);
    });
  }

  private isEmployeeIDExistValidator(c: UntypedFormControl) {
    if (!c.value) {
      return of(null);
    }

    this.employeeIdValidation = true;

    return this.employeesService.checkEmployeeId(c.value)
      .pipe(
        finalize(() => this.employeeIdValidation = false),
        map((isExists: boolean) => isExists ? {id_exists: true} : null)
      );
  }

  private isContractIDExistValidator(c: UntypedFormControl) {
    if (!c.value) {
      return of(null);
    }

    this.contractIdValidation = true;

    return this.employeesService.checkContractId(c.value)
      .pipe(
        finalize(() => this.contractIdValidation = false),
        map((isExists: boolean) => isExists ? {id_exists: true} : null)
      );
  }

  private bindConfidentialityValue(addInfoField: FormCardFieldComponent) {
    let sourceControl: UntypedFormControl;

    switch (addInfoField.descriptor.zoneID) {
      case 'cliEmp.birthDate':
        sourceControl = <UntypedFormControl>this.infoForm.get('dob');
        break;
      case 'cliEmp.firstName':
      case 'cliEmp.intFirstName':
        sourceControl = <UntypedFormControl>this.infoForm.get('firstName');
        break;
      case 'cliEmp.lastName':
      case 'cliEmp.intLastName':
        sourceControl = <UntypedFormControl>this.infoForm.get('lastName');
        break;
      case 'cliEmp.maidenName':
        sourceControl = <UntypedFormControl>this.infoForm.get('maidenName');
        break;
      case 'cliEmp.email':
        sourceControl = <UntypedFormControl>this.infoForm.get('email');
        break;
      // case 'empContract.mainContract':
      // value = !this.selectedEmployee && !this.employeeId;
      // sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      // break;
      case 'empContract.otherId':
        sourceControl = <UntypedFormControl>this.contractForm.get('contractId');
        break;
      case 'empContract.begindate':
        sourceControl = <UntypedFormControl>this.contractForm.get('beginDate');
        break;
      // case 'empContract.cliEmp':
      // value = this.selectedEmployee ? this.selectedEmployee.id : undefined;
      // sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      // break;
      // case 'empContract.cliRegNickName':
      //   sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      //   break;
      // case 'empContract.cliRegID':
      //   sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      //   break;
      case 'PayrollSets':
        sourceControl = <UntypedFormControl>this.initialForm.get('paySerie');
        break;
      // case 'empContract.payroll':
      //   sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      //   break;
      // case 'PayrollSets':
      // sourceControl = <UntypedFormControl>this.infoForm.get('Sex');
      // break;
      case 'cliEmp.otherid1':
        sourceControl = <UntypedFormControl>this.infoForm.get('number');
        break;
      case 'ForecastedEndDate':
        sourceControl = <UntypedFormControl>this.contractForm.get('forecastedEndDate');
        break;
      default:
        break;
    }

    if (!sourceControl) {
      return;
    }

    this.subscribedControls.push(
      sourceControl.valueChanges
        .pipe(startWith(sourceControl.value))
        .subscribe((v => {
          if (addInfoField.descriptor.metadatas.items.some(item => item.value === (v + ''))) {
            addInfoField.control.setValue(v);
            addInfoField.value.value = v;
          } else {
            sourceControl.setErrors({'prohibited_value': true});
            sourceControl.markAsDirty();
          }
        }))
    );
    addInfoField.readOnly = true;
    addInfoField.componentRef.instance.readOnly = true;
    addInfoField.control.disable();
  }

  private unsubscribeControls() {
    this.subscribedControls.forEach((s: Subscription) => s.unsubscribe());
    this.subscribedControls = [];
  }

  private updateEmail(): void {
    const emailControl: UntypedFormControl = <UntypedFormControl>this.infoForm.get('email');
    if (this.selectedEmployee && this.selectedEmployee.email) {
      emailControl.setValue(this.selectedEmployee.email);
      emailControl.disable();
    } else {
      emailControl.setValue('');
      const cliEmpL2IFormArray = this.cliEmpL2Form?.form?.get('cliEmpL2') as UntypedFormArray;
      cliEmpL2IFormArray?.at(0)?.get('values')?.patchValue({'cliEmp.email': {type: 'LABEL', value: ''}});
      cliEmpL2IFormArray?.updateValueAndValidity();
      emailControl.enable();
    }
    emailControl.updateValueAndValidity();
  }

  private resetForm(form: UntypedFormGroup) {
    form.reset();
    Object.keys(form.controls).forEach(empModelFamilyName => {
      form.removeControl(empModelFamilyName);
    });
  }
}
